import { useEffect, useRef, useCallback } from 'react';
import { api } from '@/services/api';
import { useChartStore } from '@/store/chartStore';
import type { FundamentalsResponse } from '@/components/Fundamentals/fundamentals.types';
import {
  getValidQuarters,
  getT4QEPS,
  computeAvgEpsEstimate,
  computeGrowthEpsEstimate,
} from '@/components/Fundamentals/fundamentals.utils';
import type { PerRegime } from '@/indicators/perLines';

/**
 * Hook: Load fundamentals data, compute EPS regimes, and store globally for indicator.
 * Handles symbol changes, cancellation of stale requests, and regime timeline construction.
 */
export function usePerLines(symbol: string, enabled: boolean = true) {
  const currentSymbolRef = useRef<string>('');
  const cancelledRef = useRef(false);
  const chart = useChartStore((s) => s.chart);

  const buildRegimes = useCallback((fundamentalsData: FundamentalsResponse): PerRegime[] => {
    const quarters = getValidQuarters(fundamentalsData.data ?? {});
    if (quarters.length === 0) return [];

    const regimes: PerRegime[] = [];

    // For each quarter, compute TTM, Average EPS, and Growth EPS estimates
    for (let i = 0; i < quarters.length; i++) {
      const q = quarters[i];
      const fiscal_period_end = q.data.fiscal_period_end;

      if (fiscal_period_end === null || fiscal_period_end === undefined) continue;

      // Day index for this quarter (floor to day bucket for alignment with daily bars)
      const dayIndex = Math.floor(fiscal_period_end / 86400);

      // TTM EPS: sum of last 4 quarters ending at quarter i
      const ttmEps = i >= 3 ? getT4QEPS(quarters, i) : null;

      // Average EPS estimate: use remaining quarters from i onward
      const avgEpsEstimate = computeAvgEpsEstimate(quarters.slice(0, i + 1));

      // Growth EPS estimate: use remaining quarters from i onward
      const growthEpsEstimate = computeGrowthEpsEstimate(quarters.slice(0, i + 1));

      regimes.push({
        dayIndex,
        ttmEps,
        growthEpsEstimate,
        avgEpsEstimate,
      });
    }

    return regimes;
  }, []);

  const fetchAndBuildRegimes = useCallback(async (sym: string) => {
    currentSymbolRef.current = sym;
    cancelledRef.current = false;

    try {
      const fundamentalsData = await api.fundamentals(sym, [
        'earnings_per_share_basic',
        'fiscal_period_end',
        'total_revenue', // For ValidQuarter filtering
      ]);

      // Discard if symbol changed or cancelled
      if (cancelledRef.current || currentSymbolRef.current !== sym) {
        return;
      }

      const regimes = buildRegimes(fundamentalsData);

      // Store in global window object for indicator calc() access
      if (!window.__per_lines_data) {
        window.__per_lines_data = {};
      }
      window.__per_lines_data[sym] = regimes;
      (window as unknown as { __per_lines_symbol: string }).__per_lines_symbol = sym;

      // Read indicatorPaneMap fresh here rather than subscribing to it — this
      // hook is mounted unconditionally for every symbol (not just when
      // PER_LINES is active), so putting it in this callback's deps would
      // re-run the whole fundamentals fetch on every indicator toggle anywhere
      // in the app, not just PER_LINES ones.
      const paneId = useChartStore.getState().indicatorPaneMap.get('PER_LINES');
      if (chart && paneId) {
        try {
          (chart as unknown as { overrideIndicator: (o: { name: string; styles?: Record<string, unknown> }, paneId?: string) => void })
            .overrideIndicator({ name: 'PER_LINES', styles: {} }, paneId);
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      console.error('[usePerLines] Failed to fetch fundamentals:', err);
      // Clear regimes on error so indicator shows no data
      if (window.__per_lines_data) {
        window.__per_lines_data[sym] = [];
      }
    }
  }, [buildRegimes, chart]);

  useEffect(() => {
    if (!enabled || !symbol) return;

    fetchAndBuildRegimes(symbol);

    return () => {
      // Mark as cancelled so any pending fetch is discarded
      cancelledRef.current = true;
    };
  }, [symbol, enabled, fetchAndBuildRegimes]);
}

/**
 * Utility: Ensure regimes are loaded for symbol before creating indicator.
 * Useful for synchronous indicator creation without waiting for async hook.
 */
export async function ensurePerLinesData(symbol: string): Promise<PerRegime[] | null> {
  try {
    const fundamentalsData = await api.fundamentals(symbol, [
      'earnings_per_share_basic',
      'fiscal_period_end',
      'total_revenue',
    ]);

    const quarters = getValidQuarters(fundamentalsData.data ?? {});
    if (quarters.length === 0) return null;

    const regimes: PerRegime[] = [];
    for (let i = 0; i < quarters.length; i++) {
      const q = quarters[i];
      const fiscal_period_end = q.data.fiscal_period_end;

      if (fiscal_period_end === null || fiscal_period_end === undefined) continue;

      const dayIndex = Math.floor(fiscal_period_end / 86400);
      const ttmEps = i >= 3 ? getT4QEPS(quarters, i) : null;
      const avgEpsEstimate = computeAvgEpsEstimate(quarters.slice(0, i + 1));
      const growthEpsEstimate = computeGrowthEpsEstimate(quarters.slice(0, i + 1));

      regimes.push({
        dayIndex,
        ttmEps,
        growthEpsEstimate,
        avgEpsEstimate,
      });
    }

    // Store globally
    if (!window.__per_lines_data) {
      window.__per_lines_data = {};
    }
    window.__per_lines_data[symbol] = regimes;
    (window as unknown as { __per_lines_symbol: string }).__per_lines_symbol = symbol;

    return regimes;
  } catch (err) {
    console.error('[ensurePerLinesData] Failed:', err);
    return null;
  }
}
